import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { Pause, Play, RotateCcw } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { haversineDistanceMeters } from '../domain/distance';
import type { AnalysisResult, TimelinePoint } from '../domain/models';
import { formatDateTime } from '../domain/time';

type MapCanvasProps = {
  result: AnalysisResult;
};

type FeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Geometry, Record<string, unknown>>;
type PlaybackSpeed = 'slow' | 'normal' | 'fast';
type PlaybackFocusMode = 'overview' | 'dynamic';
type PlaybackFrame = {
  lat: number;
  lng: number;
  timestamp: string;
};

const playbackIntervals: Record<PlaybackSpeed, number> = {
  slow: 1400,
  normal: 950,
  fast: 650,
};

const osmStyle: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    {
      id: 'osm-tiles',
      type: 'raster',
      source: 'osm',
      paint: {
        'raster-opacity': 0.92,
      },
    },
  ],
};

export function MapCanvas({ result }: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const animationRef = useRef<number | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [playheadIndex, setPlayheadIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<PlaybackSpeed>('normal');
  const [focusMode, setFocusMode] = useState<PlaybackFocusMode>('overview');
  const mapData = useMemo(() => buildMapData(result), [result]);
  const playbackFrames = useMemo(() => buildPlaybackFrames(result.points), [result.points]);
  const activeFrame = playbackFrames[Math.min(playheadIndex, Math.max(0, playbackFrames.length - 1))];
  const activeContext = useMemo(
    () => (activeFrame ? describeActiveFrame(activeFrame, result) : '再生できる時系列データがありません'),
    [activeFrame, result],
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    try {
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: osmStyle,
        attributionControl: { compact: true },
        center: [139.7671, 35.6812],
        zoom: 9,
      });

      map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
      map.on('load', () => {
        installLayers(map);
        updateMapSources(map, mapData);
        updatePlaybackSources(map, playbackFrames, playheadIndex);
        fitMap(map, mapData.bounds);
      });
      map.on('click', 'visits', (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        const title = String(feature.properties?.label ?? '滞在候補');
        const detail = String(feature.properties?.detail ?? '');
        new maplibregl.Popup({ closeButton: true }).setLngLat(event.lngLat).setText(`${title}\n${detail}`).addTo(map);
      });
      mapRef.current = map;
    } catch (error) {
      setMapError(error instanceof Error ? error.message : 'MapLibre GL を利用できません');
    }

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    setPlaying(false);
    setPlayheadIndex(0);
  }, [result.id]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    updateMapSources(map, mapData);
    fitMap(map, mapData.bounds);
  }, [mapData]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    updatePlaybackSources(map, playbackFrames, playheadIndex);
  }, [playbackFrames, playheadIndex]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    setPlaybackLayerMode(map, playing);
    if (playing) {
      focusPlaybackMap(map, mapData.bounds, playbackFrames, playheadIndex, focusMode, 900);
    }
  }, [playing, focusMode, mapData.bounds, playbackFrames, playheadIndex]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !playing || playbackFrames.length <= 1) return;

    let frameIndex = Math.min(playheadIndex, playbackFrames.length - 2);
    let segmentStartedAt = 0;
    if (focusMode === 'dynamic') {
      focusPlaybackMap(map, mapData.bounds, playbackFrames, frameIndex, focusMode, 900);
    }

    const tick = (time: number) => {
      if (segmentStartedAt === 0) segmentStartedAt = time;

      const from = playbackFrames[frameIndex];
      const to = playbackFrames[frameIndex + 1];
      const progress = Math.min(1, (time - segmentStartedAt) / playbackIntervals[speed]);
      updatePlaybackHead(map, interpolateFrame(from, to, progress));

      if (progress >= 1) {
        frameIndex += 1;
        setPlayheadIndex(frameIndex);
        segmentStartedAt = time;
        if (focusMode === 'dynamic') {
          focusPlaybackMap(map, mapData.bounds, playbackFrames, frameIndex, focusMode, playbackIntervals[speed] * 0.9);
        }

        if (frameIndex >= playbackFrames.length - 1) {
          setPlaying(false);
          return;
        }
      }

      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (animationRef.current !== null) window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    };
  }, [playing, playheadIndex, playbackFrames, speed, focusMode, mapData.bounds]);

  if (mapData.pointCount === 0) {
    return <div className="surface map-empty">表示できる位置点がありません</div>;
  }

  if (mapError) {
    return <SvgFallback result={result} message={mapError} />;
  }

  return (
    <div className="surface map-shell">
      <div className="map-canvas" ref={containerRef} aria-label="Timeline map" />
      <div className="playback-panel" aria-label="Timeline playback controls">
        <div className="playback-main">
          <button
            className="button primary playback-action"
            type="button"
            disabled={playbackFrames.length <= 1}
            onClick={() => {
              if (!playing && playheadIndex >= playbackFrames.length - 1) {
                setPlayheadIndex(0);
              }
              setPlaying((current) => !current);
            }}
            aria-label={playing ? '一時停止' : '再生'}
          >
            {playing ? <Pause size={17} aria-hidden="true" /> : <Play size={17} aria-hidden="true" />}
            {playing ? '一時停止' : '再生'}
          </button>
          <button
            className="button playback-icon"
            type="button"
            disabled={playbackFrames.length <= 1}
            onClick={() => {
              setPlaying(false);
              setPlayheadIndex(0);
            }}
            aria-label="先頭に戻る"
          >
            <RotateCcw size={16} aria-hidden="true" />
          </button>
          <div className="playback-status">
            <strong>{activeFrame ? formatDateTime(activeFrame.timestamp) : '-'}</strong>
            <span>{activeContext}</span>
          </div>
        </div>
        {playing ? <div className="playback-focus-note">再生中はフォーカス丸だけを表示しています</div> : null}
        <div className="playback-scrubber">
          <input
            type="range"
            min={0}
            max={Math.max(0, playbackFrames.length - 1)}
            value={Math.min(playheadIndex, Math.max(0, playbackFrames.length - 1))}
            disabled={playbackFrames.length <= 1}
            onChange={(event) => {
              setPlaying(false);
              setPlayheadIndex(Number(event.target.value));
            }}
            aria-label="時系列再生位置"
          />
          <select
            value={speed}
            onChange={(event) => setSpeed(event.target.value as PlaybackSpeed)}
            aria-label="再生速度"
          >
            <option value="slow">ゆっくり</option>
            <option value="normal">標準</option>
            <option value="fast">少し速く</option>
          </select>
          <select
            value={focusMode}
            onChange={(event) => setFocusMode(event.target.value as PlaybackFocusMode)}
            aria-label="フォーカスモード"
          >
            <option value="overview">全体固定</option>
            <option value="dynamic">自動フォーカス</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function installLayers(map: maplibregl.Map) {
  if (!map.getSource('route')) {
    map.addSource('route', { type: 'geojson', data: emptyFeatureCollection() });
  }
  if (!map.getSource('visits')) {
    map.addSource('visits', { type: 'geojson', data: emptyFeatureCollection() });
  }
  if (!map.getSource('points')) {
    map.addSource('points', { type: 'geojson', data: emptyFeatureCollection() });
  }
  if (!map.getSource('playback-trace')) {
    map.addSource('playback-trace', { type: 'geojson', data: emptyFeatureCollection() });
  }
  if (!map.getSource('playback-head')) {
    map.addSource('playback-head', { type: 'geojson', data: emptyFeatureCollection() });
  }

  if (!map.getLayer('route-line')) {
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      paint: {
        'line-color': '#0f6cae',
        'line-width': 3,
        'line-opacity': 0.78,
      },
    });
  }

  if (!map.getLayer('points')) {
    map.addLayer({
      id: 'points',
      type: 'circle',
      source: 'points',
      paint: {
        'circle-color': '#4b5563',
        'circle-radius': 2.5,
        'circle-opacity': 0.38,
      },
    });
  }

  if (!map.getLayer('visits')) {
    map.addLayer({
      id: 'visits',
      type: 'circle',
      source: 'visits',
      paint: {
        'circle-color': ['case', ['>=', ['get', 'confidence'], 0.8], '#0b7a53', ['>=', ['get', 'confidence'], 0.5], '#b7791f', '#b83b35'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-radius': ['interpolate', ['linear'], ['get', 'durationMinutes'], 10, 7, 120, 13, 480, 22],
        'circle-opacity': 0.88,
      },
    });
  }

  if (!map.getLayer('playback-trace-line')) {
    map.addLayer({
      id: 'playback-trace-line',
      type: 'line',
      source: 'playback-trace',
      paint: {
        'line-color': '#e05a2a',
        'line-width': 5,
        'line-opacity': 0.88,
      },
    });
  }

  if (!map.getLayer('playback-head-halo')) {
    map.addLayer({
      id: 'playback-head-halo',
      type: 'circle',
      source: 'playback-head',
      paint: {
        'circle-color': '#ffffff',
        'circle-radius': 13,
        'circle-opacity': 0.9,
      },
    });
  }

  if (!map.getLayer('playback-head-dot')) {
    map.addLayer({
      id: 'playback-head-dot',
      type: 'circle',
      source: 'playback-head',
      paint: {
        'circle-color': '#e05a2a',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-radius': 7,
      },
    });
  }
}

function updateMapSources(map: maplibregl.Map, data: ReturnType<typeof buildMapData>) {
  setGeoJson(map, 'route', data.route);
  setGeoJson(map, 'visits', data.visits);
  setGeoJson(map, 'points', data.points);
}

function setGeoJson(map: maplibregl.Map, sourceId: string, data: FeatureCollection) {
  const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
  source?.setData(data);
}

function setPlaybackLayerMode(map: maplibregl.Map, playing: boolean) {
  const hiddenDuringPlayback = ['route-line', 'points', 'visits', 'playback-trace-line'];
  for (const layerId of hiddenDuringPlayback) {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', playing ? 'none' : 'visible');
    }
  }

  if (map.getLayer('playback-head-halo')) {
    map.setPaintProperty('playback-head-halo', 'circle-radius', playing ? 17 : 13);
  }
  if (map.getLayer('playback-head-dot')) {
    map.setPaintProperty('playback-head-dot', 'circle-radius', playing ? 9 : 7);
  }
}

function updatePlaybackSources(map: maplibregl.Map, frames: PlaybackFrame[], index: number) {
  if (frames.length === 0) {
    setGeoJson(map, 'playback-trace', emptyFeatureCollection());
    setGeoJson(map, 'playback-head', emptyFeatureCollection());
    return;
  }

  const safeIndex = Math.min(index, frames.length - 1);
  const traceCoordinates = frames.slice(0, safeIndex + 1).map((frame) => [frame.lng, frame.lat]);
  if (traceCoordinates.length === 1) {
    traceCoordinates.push(traceCoordinates[0]);
  }
  const head = frames[safeIndex];

  setGeoJson(map, 'playback-trace', {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: traceCoordinates },
        properties: {},
      },
    ],
  });
  setGeoJson(map, 'playback-head', {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [head.lng, head.lat] },
        properties: { timestamp: head.timestamp },
      },
    ],
  });
}

function updatePlaybackHead(map: maplibregl.Map, frame: PlaybackFrame) {
  setGeoJson(map, 'playback-head', {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [frame.lng, frame.lat] },
        properties: { timestamp: frame.timestamp },
      },
    ],
  });
}

function fitMap(map: maplibregl.Map, bounds: maplibregl.LngLatBounds | null) {
  if (!bounds || bounds.isEmpty()) return;
  map.fitBounds(bounds, { padding: 42, maxZoom: 14, duration: 350 });
}

function fitMapForPlayback(map: maplibregl.Map, bounds: maplibregl.LngLatBounds | null) {
  if (!bounds || bounds.isEmpty()) return;
  map.fitBounds(bounds, { padding: 72, maxZoom: 5.8, duration: 900 });
}

function focusPlaybackMap(
  map: maplibregl.Map,
  fullBounds: maplibregl.LngLatBounds | null,
  frames: PlaybackFrame[],
  index: number,
  mode: PlaybackFocusMode,
  duration: number,
) {
  if (mode === 'overview') {
    fitMapForPlayback(map, fullBounds);
    return;
  }
  fitDynamicPlaybackWindow(map, frames, index, duration);
}

function fitDynamicPlaybackWindow(map: maplibregl.Map, frames: PlaybackFrame[], index: number, duration: number) {
  if (frames.length === 0) return;

  const current = frames[Math.min(index, frames.length - 1)];
  const next = frames[Math.min(index + 1, frames.length - 1)];
  const previous = frames[Math.max(index - 1, 0)];
  const nextDistanceMeters = current === next ? 0 : haversineDistanceMeters(current, next);
  const previousDistanceMeters = current === previous ? 0 : haversineDistanceMeters(previous, current);

  if (nextDistanceMeters < 350 && previousDistanceMeters < 350) {
    map.easeTo({
      center: [current.lng, current.lat],
      zoom: 12.6,
      duration,
      essential: true,
    });
    return;
  }

  const bounds = new maplibregl.LngLatBounds();
  bounds.extend([current.lng, current.lat]);
  bounds.extend([next.lng, next.lat]);

  map.fitBounds(bounds, {
    padding: dynamicFocusPadding(nextDistanceMeters),
    maxZoom: maxZoomForImmediateMove(nextDistanceMeters),
    duration,
    essential: true,
  });
}

function dynamicFocusPadding(distanceMeters: number): number {
  if (distanceMeters > 120_000) return 90;
  if (distanceMeters > 25_000) return 105;
  return 130;
}

function maxZoomForImmediateMove(distanceMeters: number): number {
  if (distanceMeters > 250_000) return 5.7;
  if (distanceMeters > 120_000) return 6.5;
  if (distanceMeters > 40_000) return 8.2;
  if (distanceMeters > 10_000) return 10;
  if (distanceMeters > 2_000) return 11.6;
  return 12.8;
}

function buildMapData(result: AnalysisResult) {
  const bounds = new maplibregl.LngLatBounds();
  const routeFeatures: FeatureCollection['features'] = result.segments.map((segment) => {
    const coordinates = segment.path.map((point) => {
      bounds.extend([point.lng, point.lat]);
      return [point.lng, point.lat];
    });
    return {
      type: 'Feature' as const,
      geometry: { type: 'LineString' as const, coordinates },
      properties: {
        id: segment.id,
        distanceMeters: segment.distanceMeters,
      },
    };
  });

  const sampledPoints = samplePoints(result.points, 6000);
  const pointFeatures: FeatureCollection['features'] = sampledPoints.map((point) => {
    bounds.extend([point.lng, point.lat]);
    return {
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [point.lng, point.lat] },
      properties: { id: point.id, timestamp: point.timestamp },
    };
  });

  const visitFeatures: FeatureCollection['features'] = result.visits.map((visit) => {
    bounds.extend([visit.center.lng, visit.center.lat]);
    return {
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [visit.center.lng, visit.center.lat] },
      properties: {
        id: visit.id,
        label: visit.placeName ?? `${visit.center.lat.toFixed(4)}, ${visit.center.lng.toFixed(4)}`,
        detail: `${formatDateTime(visit.startTime)} - ${formatDateTime(visit.endTime)} / 約 ${Math.round(visit.durationMinutes)} 分`,
        durationMinutes: visit.durationMinutes,
        confidence: visit.confidence,
      },
    };
  });

  return {
    route: { type: 'FeatureCollection' as const, features: routeFeatures },
    points: { type: 'FeatureCollection' as const, features: pointFeatures },
    visits: { type: 'FeatureCollection' as const, features: visitFeatures },
    bounds,
    pointCount: result.points.length,
  };
}

function emptyFeatureCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function samplePoints<T>(points: T[], maxPoints: number): T[] {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  return points.filter((_, index) => index % step === 0);
}

function buildPlaybackFrames(points: TimelinePoint[], maxFrames = 900): PlaybackFrame[] {
  const sorted = [...points]
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp))
    .map((point) => ({ lat: point.lat, lng: point.lng, timestamp: point.timestamp }));
  if (sorted.length <= maxFrames) return sorted;
  const step = Math.ceil(sorted.length / maxFrames);
  return sorted.filter((_, index) => index % step === 0);
}

function interpolateFrame(from: PlaybackFrame, to: PlaybackFrame, progress: number): PlaybackFrame {
  return {
    lat: from.lat + (to.lat - from.lat) * progress,
    lng: from.lng + (to.lng - from.lng) * progress,
    timestamp: progress < 0.5 ? from.timestamp : to.timestamp,
  };
}

function describeActiveFrame(frame: PlaybackFrame, result: AnalysisResult): string {
  const timestamp = Date.parse(frame.timestamp);
  const visit = result.visits.find((candidate) => Date.parse(candidate.startTime) <= timestamp && Date.parse(candidate.endTime) >= timestamp);
  if (visit) {
    return `滞在中: ${visit.placeName ?? `${visit.center.lat.toFixed(3)}, ${visit.center.lng.toFixed(3)} 周辺`}`;
  }

  const segment = result.segments.find((candidate) => Date.parse(candidate.startTime) <= timestamp && Date.parse(candidate.endTime) >= timestamp);
  if (segment) {
    return `移動中: ${segment.inferredMode ?? 'unknown'} / 約 ${Math.round(segment.distanceMeters / 1000)} km`;
  }

  return `${frame.lat.toFixed(3)}, ${frame.lng.toFixed(3)} 周辺`;
}

function SvgFallback({ result, message }: { result: AnalysisResult; message: string }) {
  const bounds = computeBounds(result);
  const width = 1000;
  const height = 560;
  const project = (lat: number, lng: number) => {
    const x = ((lng - bounds.minLng) / Math.max(0.0001, bounds.maxLng - bounds.minLng)) * (width - 80) + 40;
    const y = height - (((lat - bounds.minLat) / Math.max(0.0001, bounds.maxLat - bounds.minLat)) * (height - 80) + 40);
    return `${x},${y}`;
  };

  return (
    <div className="surface map-shell">
      <svg viewBox={`0 0 ${width} ${height}`} className="map-canvas" role="img" aria-label="Map fallback">
        <rect width={width} height={height} fill="#eef3f0" />
        <text x="40" y="42" fill="#5b6864" fontSize="18">
          {message}
        </text>
        {result.segments.map((segment) => (
          <polyline
            key={segment.id}
            points={segment.path.map((point) => project(point.lat, point.lng)).join(' ')}
            fill="none"
            stroke="#0f6cae"
            strokeWidth="4"
            strokeOpacity="0.75"
          />
        ))}
        {result.visits.map((visit) => {
          const [cx, cy] = project(visit.center.lat, visit.center.lng).split(',').map(Number);
          return <circle key={visit.id} cx={cx} cy={cy} r={7 + Math.min(14, visit.durationMinutes / 45)} fill="#0b7a53" opacity="0.85" />;
        })}
      </svg>
    </div>
  );
}

function computeBounds(result: AnalysisResult) {
  const coordinates = [
    ...result.points.map((point) => ({ lat: point.lat, lng: point.lng })),
    ...result.visits.map((visit) => visit.center),
  ];
  return coordinates.reduce(
    (bounds, point) => ({
      minLat: Math.min(bounds.minLat, point.lat),
      maxLat: Math.max(bounds.maxLat, point.lat),
      minLng: Math.min(bounds.minLng, point.lng),
      maxLng: Math.max(bounds.maxLng, point.lng),
    }),
    { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 },
  );
}
